/**
 * Text aus einer PDF-Datei lesen (PARITAET-R3-3, „PDF-Import“ des bisherigen Rechnungsgenerators), ohne Fremdbibliothek:
 * Objekte und Objektströme zerlegen, FlateDecode entpacken (DecompressionStream des Browsers bzw. von Node), Seiten in
 * Reihenfolge, Textoperatoren mit Text- und Grafikmatrix auswerten, Zeichen über ToUnicode, WinAnsi oder die Differences
 * der Schrift in Unicode übersetzen und daraus Zeilen in Lesereihenfolge bilden.
 *
 * Grenzen (bewusst): gescannte Rechnungen (nur Bild, kein Text) und verschlüsselte PDFs liefern keinen Text; dann bleibt
 * es beim Eintippen. Das Ergebnis ist immer nur ein Vorschlag, die Datei selbst bleibt das Original.
 */

export type Inflate = (data: Uint8Array) => Promise<Uint8Array>;

// ── Objekte ─────────────────────────────────────────────────────────────────────────────────────────────────────────

type Val =
  | { t: "num"; v: number }
  | { t: "name"; v: string }
  | { t: "str"; v: string }          // Bytes als Zeichen 0–255
  | { t: "arr"; v: Val[] }
  | { t: "dict"; v: Map<string, Val> }
  | { t: "ref"; n: number }
  | { t: "bool"; v: boolean }
  | { t: "null" }
  | { t: "op"; v: string };

const WS = /[\0\t\n\f\r ]/;
const DELIM = /[()<>[\]{}/%]/;

class Lexer {
  pos = 0;
  constructor(readonly s: string) {}
  skip() {
    const s = this.s;
    for (;;) {
      while (this.pos < s.length && WS.test(s[this.pos]!)) this.pos++;
      if (s[this.pos] === "%") { while (this.pos < s.length && s[this.pos] !== "\n" && s[this.pos] !== "\r") this.pos++; continue; }
      return;
    }
  }
  /** nächster Wert; Operatoren (Wörter, ' und ") als „op“ */
  next(): Val | null {
    this.skip();
    const s = this.s;
    if (this.pos >= s.length) return null;
    const c = s[this.pos]!;
    if (c === "/") {
      let j = this.pos + 1;
      while (j < s.length && !WS.test(s[j]!) && !DELIM.test(s[j]!)) j++;
      const raw = s.slice(this.pos + 1, j);
      this.pos = j;
      return { t: "name", v: raw.replace(/#([0-9a-fA-F]{2})/g, (_, h: string) => String.fromCharCode(parseInt(h, 16))) };
    }
    if (c === "(") return { t: "str", v: this.literal() };
    if (c === "<") {
      if (s[this.pos + 1] === "<") {
        this.pos += 2;
        const m = new Map<string, Val>();
        for (;;) {
          this.skip();
          if (s.startsWith(">>", this.pos)) { this.pos += 2; break; }
          const k = this.next();
          if (!k) break;
          if (k.t !== "name") continue;
          const v = this.value();
          if (v) m.set(k.v, v);
        }
        return { t: "dict", v: m };
      }
      const end = s.indexOf(">", this.pos);
      const hex = s.slice(this.pos + 1, end < 0 ? s.length : end).replace(/[^0-9a-fA-F]/g, "");
      this.pos = end < 0 ? s.length : end + 1;
      let out = "";
      for (let i = 0; i < hex.length; i += 2) out += String.fromCharCode(parseInt((hex.slice(i, i + 2) + "0").slice(0, 2), 16));
      return { t: "str", v: out };
    }
    if (c === "[") {
      this.pos++;
      const arr: Val[] = [];
      for (;;) {
        this.skip();
        if (s[this.pos] === "]" || this.pos >= s.length) { this.pos++; break; }
        const v = this.value();
        if (!v) break;
        arr.push(v);
      }
      return { t: "arr", v: arr };
    }
    if (c === "]" || c === ">" || c === ")" || c === "{" || c === "}") { this.pos++; return this.next(); }
    let j = this.pos;
    while (j < s.length && !WS.test(s[j]!) && !DELIM.test(s[j]!)) j++;
    if (j === this.pos) j++;
    const w = s.slice(this.pos, j);
    this.pos = j;
    if (/^[+-]?(\d+\.?\d*|\.\d+)$/.test(w)) return { t: "num", v: Number(w) };
    if (w === "true" || w === "false") return { t: "bool", v: w === "true" };
    if (w === "null") return { t: "null" };
    return { t: "op", v: w };
  }
  /** Wert mit Auflösung von „n g R“ */
  value(): Val | null {
    const v = this.next();
    if (v?.t === "num" && Number.isInteger(v.v)) {
      const save = this.pos;
      const g = this.next();
      if (g?.t === "num") {
        const r = this.next();
        if (r?.t === "op" && r.v === "R") return { t: "ref", n: v.v };
      }
      this.pos = save;
    }
    return v;
  }
  literal(): string {
    const s = this.s;
    let depth = 0;
    let out = "";
    this.pos++;
    while (this.pos < s.length) {
      const c = s[this.pos++]!;
      if (c === "\\") {
        const n = s[this.pos++]!;
        const map: Record<string, string> = { n: "\n", r: "\r", t: "\t", b: "\b", f: "\f", "(": "(", ")": ")", "\\": "\\" };
        if (n in map) out += map[n]!;
        else if (/[0-7]/.test(n)) {
          let oct = n;
          while (oct.length < 3 && /[0-7]/.test(s[this.pos] ?? "")) oct += s[this.pos++];
          out += String.fromCharCode(parseInt(oct, 8) & 0xff);
        } else if (n === "\r") { if (s[this.pos] === "\n") this.pos++; }
        else if (n !== "\n") out += n;
        continue;
      }
      if (c === "(") depth++;
      if (c === ")") { if (depth === 0) break; depth--; }
      out += c;
    }
    return out;
  }
}

interface PdfObj { dict: Val | null; stream: string | null }

function latin1(bytes: Uint8Array): string {
  let s = "";
  const CH = 0x8000;
  for (let i = 0; i < bytes.length; i += CH) s += String.fromCharCode(...bytes.subarray(i, i + CH));
  return s;
}
function toBytes(s: string): Uint8Array {
  const b = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) b[i] = s.charCodeAt(i) & 0xff;
  return b;
}

/** Entpacken mit DecompressionStream (zlib); bei Fehler am Ende bleibt, was schon entpackt ist. */
export const defaultInflate: Inflate = async (data) => {
  const DS = (globalThis as { DecompressionStream?: new (f: string) => TransformStream<Uint8Array, Uint8Array> }).DecompressionStream;
  if (!DS) throw new Error("Entpacken wird von diesem Browser nicht unterstützt.");
  const run = async (fmt: string, bytes: Uint8Array) => {
    const reader = new Blob([bytes as BlobPart]).stream().pipeThrough(new DS(fmt)).getReader();
    const parts: Uint8Array[] = [];
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        parts.push(value);
      }
    } catch (e) {
      if (!parts.length) throw e;
    }
    const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
    let o = 0;
    for (const p of parts) { out.set(p, o); o += p.length; }
    return out;
  };
  try {
    return await run("deflate", data);
  } catch {
    return run("deflate-raw", data.subarray(2));
  }
};

class PdfFile {
  objs = new Map<number, PdfObj>();
  constructor(readonly src: string, readonly inflate: Inflate) {}

  parse() {
    const s = this.src;
    const re = /(\d+)\s+(\d+)\s+obj\b/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(s)) !== null) {
      const num = Number(m[1]);
      const lx = new Lexer(s);
      lx.pos = m.index + m[0].length;
      const dict = lx.value();
      lx.skip();
      let stream: string | null = null;
      if (s.startsWith("stream", lx.pos)) {
        let start = lx.pos + 6;
        if (s[start] === "\r") start++;
        if (s[start] === "\n") start++;
        const lenV = dict?.t === "dict" ? dict.v.get("Length") : undefined;
        let end = -1;
        if (lenV?.t === "num") {
          const after = s.slice(start + lenV.v, start + lenV.v + 20);
          if (/^\s*endstream/.test(after)) end = start + lenV.v;
        }
        if (end < 0) {
          end = s.indexOf("endstream", start);
          if (end < 0) end = s.length;
          while (end > start && (s[end - 1] === "\n" || s[end - 1] === "\r")) end--;
        }
        stream = s.slice(start, end);
        re.lastIndex = end;
      }
      this.objs.set(num, { dict, stream });
    }
  }

  get(v: Val | undefined | null): Val | null {
    let cur = v ?? null;
    for (let i = 0; i < 8 && cur?.t === "ref"; i++) cur = this.objs.get(cur.n)?.dict ?? null;
    return cur;
  }
  dict(v: Val | undefined | null): Map<string, Val> | null {
    const d = this.get(v);
    return d?.t === "dict" ? d.v : null;
  }

  async data(o: PdfObj): Promise<string> {
    if (o.stream === null) return "";
    const d = o.dict?.t === "dict" ? o.dict.v : new Map<string, Val>();
    const f = this.get(d.get("Filter"));
    const filters = f?.t === "name" ? [f.v] : f?.t === "arr" ? f.v.map((x) => (x.t === "name" ? x.v : "")) : [];
    let bytes = toBytes(o.stream);
    for (const name of filters) {
      if (name === "FlateDecode" || name === "Fl") bytes = await this.inflate(bytes);
      else if (name === "ASCIIHexDecode" || name === "AHx") {
        const hex = latin1(bytes).replace(/>[\s\S]*$/, "").replace(/[^0-9a-fA-F]/g, "");
        bytes = new Uint8Array(Math.ceil(hex.length / 2)).map((_, i) => parseInt((hex.slice(i * 2, i * 2 + 2) + "0").slice(0, 2), 16));
      }
      else return "";   // Bilder (DCT, JBIG2 …) und Unbekanntes: kein Text
    }
    return latin1(bytes);
  }

  /** Objektströme (PDF 1.5) auspacken */
  async expandObjectStreams() {
    for (const [, o] of [...this.objs]) {
      const d = o.dict?.t === "dict" ? o.dict.v : null;
      const type = d?.get("Type");
      if (!d || type?.t !== "name" || type.v !== "ObjStm") continue;
      let text: string;
      try { text = await this.data(o); } catch { continue; }
      const n = (d.get("N") as { v: number } | undefined)?.v ?? 0;
      const first = (d.get("First") as { v: number } | undefined)?.v ?? 0;
      const head = new Lexer(text.slice(0, first));
      const pairs: [number, number][] = [];
      for (let i = 0; i < n; i++) {
        const a = head.next(); const b = head.next();
        if (a?.t !== "num" || b?.t !== "num") break;
        pairs.push([a.v, b.v]);
      }
      for (const [num, off] of pairs) {
        if (this.objs.has(num) && this.objs.get(num)!.dict) continue;
        const lx = new Lexer(text);
        lx.pos = first + off;
        this.objs.set(num, { dict: lx.value(), stream: null });
      }
    }
  }

  /** Seiten in Reihenfolge (Seitenbaum), sonst in Dateireihenfolge */
  pages(): Map<string, Val>[] {
    const out: Map<string, Val>[] = [];
    const catalog = [...this.objs.values()].map((o) => (o.dict?.t === "dict" ? o.dict.v : null)).find((d) => d?.get("Type")?.t === "name" && (d.get("Type") as { v: string }).v === "Catalog");
    const walk = (node: Map<string, Val> | null, depth: number) => {
      if (!node || depth > 20) return;
      const type = node.get("Type");
      if (type?.t === "name" && type.v === "Page") { out.push(node); return; }
      const kids = this.get(node.get("Kids"));
      if (kids?.t === "arr") for (const k of kids.v) walk(this.dict(k), depth + 1);
    };
    if (catalog) walk(this.dict(catalog.get("Pages")), 0);
    if (!out.length) {
      for (const o of this.objs.values()) {
        const d = o.dict?.t === "dict" ? o.dict.v : null;
        const t = d?.get("Type");
        if (d && t?.t === "name" && t.v === "Page") out.push(d);
      }
    }
    return out;
  }
  /** geerbte Eigenschaft einer Seite (Resources) */
  inherited(page: Map<string, Val>, key: string): Val | null {
    let node: Map<string, Val> | null = page;
    for (let i = 0; node && i < 20; i++) {
      const v = node.get(key);
      if (v) return this.get(v);
      node = this.dict(node.get("Parent"));
    }
    return null;
  }
}

// ── Schriften ───────────────────────────────────────────────────────────────────────────────────────────────────────

const WIN_ANSI_HIGH: Record<number, string> = {
  0x80: "€", 0x82: "‚", 0x83: "ƒ", 0x84: "„", 0x85: "…", 0x86: "†", 0x87: "‡", 0x88: "ˆ", 0x89: "‰", 0x8a: "Š", 0x8b: "‹", 0x8c: "Œ",
  0x8e: "Ž", 0x91: "‘", 0x92: "’", 0x93: "“", 0x94: "”", 0x95: "•", 0x96: "–", 0x97: "—", 0x98: "˜", 0x99: "™", 0x9a: "š", 0x9b: "›",
  0x9c: "œ", 0x9e: "ž", 0x9f: "Ÿ",
};
const winAnsi = (b: number) => (b >= 0x80 && b <= 0x9f ? WIN_ANSI_HIGH[b] ?? "" : String.fromCharCode(b));

const GLYPHS: Record<string, string> = {
  space: " ", exclam: "!", quotedbl: "\"", numbersign: "#", dollar: "$", percent: "%", ampersand: "&", quotesingle: "'", quoteright: "’",
  parenleft: "(", parenright: ")", asterisk: "*", plus: "+", comma: ",", hyphen: "-", minus: "-", period: ".", slash: "/", colon: ":",
  semicolon: ";", less: "<", equal: "=", greater: ">", question: "?", at: "@", bracketleft: "[", backslash: "\\", bracketright: "]",
  underscore: "_", quoteleft: "‘", braceleft: "{", bar: "|", braceright: "}", asciitilde: "~", Euro: "€", euro: "€", endash: "–", emdash: "—",
  bullet: "•", ellipsis: "…", quotedblleft: "“", quotedblright: "”", quotedblbase: "„", quotesinglbase: "‚", section: "§", degree: "°",
  germandbls: "ß", adieresis: "ä", odieresis: "ö", udieresis: "ü", Adieresis: "Ä", Odieresis: "Ö", Udieresis: "Ü", eacute: "é", egrave: "è",
  aacute: "á", agrave: "à", oacute: "ó", uacute: "ú", ccedilla: "ç", ntilde: "ñ", Eacute: "É", nbspace: " ", nonbreakingspace: " ",
  zero: "0", one: "1", two: "2", three: "3", four: "4", five: "5", six: "6", seven: "7", eight: "8", nine: "9", multiply: "×",
  copyright: "©", registered: "®", trademark: "™", sterling: "£", yen: "¥", paragraph: "¶", periodcentered: "·", guillemotleft: "«",
  guillemotright: "»", fi: "fi", fl: "fl",
};
function glyphToUnicode(name: string): string {
  if (GLYPHS[name] !== undefined) return GLYPHS[name]!;
  if (/^[A-Za-z]$/.test(name)) return name;
  const uni = /^uni([0-9A-Fa-f]{4,6})$/.exec(name) ?? /^u([0-9A-Fa-f]{4,6})$/.exec(name);
  if (uni) return String.fromCodePoint(parseInt(uni[1]!, 16));
  const base = name.split(".")[0]!;
  return base !== name ? glyphToUnicode(base) : "";
}

interface Font {
  /** Code → Text; ohne Eintrag gilt die einfache Kodierung */
  map: Map<number, string>;
  /** Bytes je Zeichencode (Type0 meist 2) */
  bytes: 1 | 2;
  simple: ((b: number) => string) | null;
  widths: Map<number, number>;
  defaultWidth: number;
}

function parseCMap(text: string): { map: Map<number, string>; bytes: 1 | 2 } {
  const map = new Map<number, string>();
  const hexToStr = (h: string) => {
    let out = "";
    for (let i = 0; i + 4 <= h.length; i += 4) out += String.fromCharCode(parseInt(h.slice(i, i + 4), 16));
    if (h.length === 2) out = String.fromCharCode(parseInt(h, 16));
    return out;
  };
  let bytes: 1 | 2 = 1;
  const cs = /begincodespacerange([\s\S]*?)endcodespacerange/.exec(text);
  if (cs && /<[0-9a-fA-F]{4}>/.test(cs[1]!)) bytes = 2;
  for (const block of text.matchAll(/beginbfchar([\s\S]*?)endbfchar/g)) {
    for (const m of block[1]!.matchAll(/<([0-9a-fA-F]+)>\s*<([0-9a-fA-F]*)>/g)) {
      map.set(parseInt(m[1]!, 16), hexToStr(m[2]!));
      if (m[1]!.length >= 4) bytes = 2;
    }
  }
  for (const block of text.matchAll(/beginbfrange([\s\S]*?)endbfrange/g)) {
    for (const m of block[1]!.matchAll(/<([0-9a-fA-F]+)>\s*<([0-9a-fA-F]+)>\s*(<[0-9a-fA-F]*>|\[[^\]]*\])/g)) {
      const lo = parseInt(m[1]!, 16), hi = parseInt(m[2]!, 16);
      if (m[1]!.length >= 4) bytes = 2;
      if (hi - lo > 5000) continue;
      if (m[3]!.startsWith("[")) {
        const items = [...m[3]!.matchAll(/<([0-9a-fA-F]*)>/g)].map((x) => hexToStr(x[1]!));
        items.forEach((s, i) => map.set(lo + i, s));
      } else {
        const start = hexToStr(m[3]!.slice(1, -1));
        const last = start.charCodeAt(start.length - 1);
        for (let c = lo; c <= hi; c++) map.set(c, start.slice(0, -1) + String.fromCharCode(last + (c - lo)));
      }
    }
  }
  return { map, bytes };
}

async function loadFont(pdf: PdfFile, ref: Val | undefined): Promise<Font> {
  const d = pdf.dict(ref);
  const font: Font = { map: new Map(), bytes: 1, simple: winAnsi, widths: new Map(), defaultWidth: 500 };
  if (!d) return font;
  const subtype = pdf.get(d.get("Subtype"));
  const type0 = subtype?.t === "name" && subtype.v === "Type0";
  if (type0) { font.bytes = 2; font.simple = null; font.defaultWidth = 1000; }
  const tu = d.get("ToUnicode");
  if (tu?.t === "ref") {
    const o = pdf.objs.get(tu.n);
    if (o) {
      try {
        const cm = parseCMap(await pdf.data(o));
        font.map = cm.map;
        if (!type0) font.bytes = cm.bytes;
      } catch { /* ohne Zuordnung */ }
    }
  }
  const enc = pdf.get(d.get("Encoding"));
  if (!type0 && enc?.t === "dict") {
    const diffs = pdf.get(enc.v.get("Differences"));
    if (diffs?.t === "arr") {
      const table = new Map<number, string>();
      let code = 0;
      for (const x of diffs.v) {
        if (x.t === "num") code = x.v;
        else if (x.t === "name") { table.set(code, glyphToUnicode(x.v)); code++; }
      }
      font.simple = (b) => (table.has(b) ? table.get(b)! : winAnsi(b));
    }
  }
  // Breiten für die Abstände zwischen Textstücken
  if (!type0) {
    const first = pdf.get(d.get("FirstChar"));
    const w = pdf.get(d.get("Widths"));
    if (first?.t === "num" && w?.t === "arr") w.v.forEach((x, i) => { const v = pdf.get(x); if (v?.t === "num") font.widths.set(first.v + i, v.v); });
  } else {
    const desc = pdf.get(d.get("DescendantFonts"));
    const cid = desc?.t === "arr" ? pdf.dict(desc.v[0]) : null;
    const dw = pdf.get(cid?.get("DW"));
    if (dw?.t === "num") font.defaultWidth = dw.v;
    const W = pdf.get(cid?.get("W"));
    if (W?.t === "arr") {
      for (let i = 0; i < W.v.length;) {
        const a = pdf.get(W.v[i]); const b = pdf.get(W.v[i + 1]);
        if (a?.t === "num" && b?.t === "arr") { b.v.forEach((x, k) => { const v = pdf.get(x); if (v?.t === "num") font.widths.set(a.v + k, v.v); }); i += 2; }
        else if (a?.t === "num" && b?.t === "num") { const c = pdf.get(W.v[i + 2]); if (c?.t === "num") for (let k = a.v; k <= b.v && k - a.v < 5000; k++) font.widths.set(k, c.v); i += 3; }
        else break;
      }
    }
  }
  return font;
}

function decode(font: Font, s: string): { text: string; width: number; spaces: number } {
  let text = "";
  let width = 0;
  let spaces = 0;
  for (let i = 0; i < s.length; i += font.bytes) {
    const code = font.bytes === 2 ? (s.charCodeAt(i) << 8) | (s.charCodeAt(i + 1) & 0xff) : s.charCodeAt(i);
    const ch = font.map.get(code) ?? (font.simple ? font.simple(code) : "");
    text += ch;
    if (ch === " ") spaces++;
    width += font.widths.get(code) ?? font.defaultWidth;
  }
  return { text, width, spaces };
}

// ── Seiteninhalt ────────────────────────────────────────────────────────────────────────────────────────────────────

type M = [number, number, number, number, number, number];
const mul = (a: M, b: M): M => [a[0] * b[0] + a[1] * b[2], a[0] * b[1] + a[1] * b[3], a[2] * b[0] + a[3] * b[2], a[2] * b[1] + a[3] * b[3],
  a[4] * b[0] + a[5] * b[2] + b[4], a[4] * b[1] + a[5] * b[3] + b[5]];
const ID: M = [1, 0, 0, 1, 0, 0];

interface Run { x: number; y: number; end: number; size: number; text: string }

async function pageRuns(pdf: PdfFile, page: Map<string, Val>): Promise<Run[]> {
  const res = pdf.inherited(page, "Resources");
  const fontsDict = res?.t === "dict" ? pdf.dict(res.v.get("Font")) : null;
  const fonts = new Map<string, Font>();
  if (fontsDict) for (const [k, v] of fontsDict) fonts.set(k, await loadFont(pdf, v));
  const contents = pdf.get(page.get("Contents"));
  const refs = contents?.t === "arr" ? contents.v : page.get("Contents") ? [page.get("Contents")!] : [];
  let text = "";
  for (const r of refs) {
    const o = r.t === "ref" ? pdf.objs.get(r.n) : undefined;
    if (o) { try { text += `${await pdf.data(o)}\n`; } catch { /* unlesbarer Teil */ } }
  }
  const runs: Run[] = [];
  const lx = new Lexer(text);
  const stack: Val[] = [];
  let ctm: M = ID;
  const gstack: M[] = [];
  let tm: M = ID, tlm: M = ID;
  let font: Font = { map: new Map(), bytes: 1, simple: winAnsi, widths: new Map(), defaultWidth: 500 };
  let size = 10, leading = 0, tc = 0, tw = 0, th = 1, rise = 0;
  const nums = () => stack.map((v) => (v.t === "num" ? v.v : 0));
  const show = (s: string) => {
    const { text: str, width, spaces } = decode(font, s);
    const m = mul([size * th, 0, 0, size, 0, rise], mul(tm, ctm));
    const x = m[4], y = m[5];
    const adv = (width / 1000) * size + tc * s.length / font.bytes + tw * spaces;
    tm = mul([1, 0, 0, 1, adv * th, 0], tm);
    const e = mul([1, 0, 0, 1, 0, 0], mul(tm, ctm));
    const eff = Math.hypot(m[2], m[3]) || size;
    if (str) runs.push({ x, y, end: e[4], size: eff, text: str });
  };
  for (let guard = 0; guard < 2_000_000; guard++) {
    const v = lx.next();
    if (!v) break;
    if (v.t !== "op") { stack.push(v); continue; }
    const n = nums();
    switch (v.v) {
      case "q": gstack.push(ctm); break;
      case "Q": ctm = gstack.pop() ?? ID; break;
      case "cm": if (n.length >= 6) ctm = mul(n.slice(-6) as M, ctm); break;
      case "BT": tm = ID; tlm = ID; break;
      case "Tf": { const f = stack[stack.length - 2]; if (f?.t === "name") font = fonts.get(f.v) ?? font; size = n[n.length - 1] ?? size; break; }
      case "Tc": tc = n[0] ?? 0; break;
      case "Tw": tw = n[0] ?? 0; break;
      case "Tz": th = (n[0] ?? 100) / 100; break;
      case "TL": leading = n[0] ?? 0; break;
      case "Ts": rise = n[0] ?? 0; break;
      case "Td": tlm = mul([1, 0, 0, 1, n[0] ?? 0, n[1] ?? 0], tlm); tm = tlm; break;
      case "TD": leading = -(n[1] ?? 0); tlm = mul([1, 0, 0, 1, n[0] ?? 0, n[1] ?? 0], tlm); tm = tlm; break;
      case "Tm": if (n.length >= 6) { tlm = n.slice(-6) as M; tm = tlm; } break;
      case "T*": tlm = mul([1, 0, 0, 1, 0, -leading], tlm); tm = tlm; break;
      case "Tj": { const s = stack[stack.length - 1]; if (s?.t === "str") show(s.v); break; }
      case "'": { tlm = mul([1, 0, 0, 1, 0, -leading], tlm); tm = tlm; const s = stack[stack.length - 1]; if (s?.t === "str") show(s.v); break; }
      case "\"": { tw = n[0] ?? tw; tc = n[1] ?? tc; tlm = mul([1, 0, 0, 1, 0, -leading], tlm); tm = tlm; const s = stack[stack.length - 1]; if (s?.t === "str") show(s.v); break; }
      case "TJ": {
        const a = stack[stack.length - 1];
        if (a?.t === "arr") {
          for (const x of a.v) {
            if (x.t === "str") show(x.v);
            else if (x.t === "num") tm = mul([1, 0, 0, 1, (-x.v / 1000) * size * th, 0], tm);
          }
        }
        break;
      }
      case "BI": {
        // eingebettetes Bild überspringen (Binärdaten von „ID“ bis „EI“)
        const at = text.indexOf("ID", lx.pos);
        const r = /\sEI(?=\s|$)/g;
        r.lastIndex = at < 0 ? text.length : at + 2;
        const mm = at < 0 ? null : r.exec(text);
        lx.pos = mm ? mm.index + 3 : text.length;
        break;
      }
      default: break;
    }
    stack.length = 0;
  }
  return runs;
}

/** Textstücke einer Seite zu Zeilen: von oben nach unten, links nach rechts; große Lücken werden zu zwei Leerzeichen. */
function linesOf(runs: Run[]): string[] {
  const sorted = [...runs].sort((a, b) => b.y - a.y || a.x - b.x);
  const rows: { y: number; size: number; runs: Run[] }[] = [];
  for (const r of sorted) {
    const row = rows.find((x) => Math.abs(x.y - r.y) < Math.max(2, Math.min(x.size, r.size) * 0.45));
    if (row) row.runs.push(r);
    else rows.push({ y: r.y, size: r.size, runs: [r] });
  }
  rows.sort((a, b) => b.y - a.y);
  return rows.map((row) => {
    const rs = row.runs.sort((a, b) => a.x - b.x);
    let out = "";
    let prevEnd: number | null = null;
    for (const r of rs) {
      if (prevEnd !== null) {
        const gap = r.x - prevEnd;
        if (gap > r.size * 1.2) out += "  ";
        else if (gap > r.size * 0.15 && !out.endsWith(" ") && !r.text.startsWith(" ")) out += " ";
      }
      out += r.text;
      prevEnd = Math.max(prevEnd ?? -Infinity, r.end);
    }
    return out.replace(/\s+$/, "").replace(/^\s+/, "");
  }).filter((l) => l.trim() !== "");
}

/** Alle Textzeilen einer PDF-Datei in Lesereihenfolge (Seite für Seite). Leeres Ergebnis: Bild-PDF oder unlesbar. */
export async function extractPdfLines(bytes: Uint8Array, opts: { inflate?: Inflate; maxPages?: number } = {}): Promise<string[]> {
  const src = latin1(bytes);
  if (!src.startsWith("%PDF")) throw new Error("Keine PDF-Datei.");
  if (/\/Encrypt\s/.test(src)) throw new Error("Die PDF-Datei ist verschlüsselt und lässt sich nicht einlesen.");
  const pdf = new PdfFile(src, opts.inflate ?? defaultInflate);
  pdf.parse();
  await pdf.expandObjectStreams();
  const out: string[] = [];
  for (const page of pdf.pages().slice(0, opts.maxPages ?? 10)) out.push(...linesOf(await pageRuns(pdf, page)));
  return out;
}

/** Name aus einem PDF-Text (UTF-16BE mit BOM, sonst vereinfacht Latin-1) */
function pdfTextString(s: string): string {
  if (s.charCodeAt(0) === 0xfe && s.charCodeAt(1) === 0xff) {
    let out = "";
    for (let i = 2; i + 1 < s.length; i += 2) out += String.fromCharCode((s.charCodeAt(i) << 8) | s.charCodeAt(i + 1));
    return out;
  }
  return s;
}

/**
 * Eingebettete Dateien eines PDF (Steuerprüfung L12, ZUGFeRD/Factur-X: PDF/A-3 mit XML-Anhang): alle Dateispezifikationen
 * mit /EF, gleich ob über /Names /EmbeddedFiles oder /AF eingebunden, mit Namen (/UF vor /F) und entpacktem Inhalt.
 * Nichts wird ausgeführt; verschlüsselte PDFs liefern keine Anhänge. Die Obergrenze für entpackte Ströme kommt über `inflate`.
 */
export async function extractPdfEmbeddedFiles(bytes: Uint8Array, opts: { inflate?: Inflate; maxFiles?: number } = {}): Promise<{ name: string; bytes: Uint8Array }[]> {
  const src = latin1(bytes);
  if (!src.slice(0, 1024).includes("%PDF-")) throw new Error("Keine PDF-Datei.");
  if (/\/Encrypt\s/.test(src)) return [];
  const pdf = new PdfFile(src, opts.inflate ?? defaultInflate);
  pdf.parse();
  await pdf.expandObjectStreams();
  const out: { name: string; bytes: Uint8Array }[] = [];
  const seen = new Set<number>();
  for (const o of pdf.objs.values()) {
    const d = o.dict?.t === "dict" ? o.dict.v : null;
    const ef = d ? pdf.dict(d.get("EF")) : null;
    if (!d || !ef) continue;
    const ref = ef.get("UF") ?? ef.get("F");
    if (ref?.t !== "ref" || seen.has(ref.n)) continue;
    const stream = pdf.objs.get(ref.n);
    if (!stream || stream.stream === null) continue;
    seen.add(ref.n);
    const nameVal = pdf.get(d.get("UF")) ?? pdf.get(d.get("F"));
    const name = nameVal?.t === "str" ? pdfTextString(nameVal.v) : "";
    out.push({ name: name.split(/[\\/]/).pop() ?? name, bytes: toBytes(await pdf.data(stream)) });
    if (out.length >= (opts.maxFiles ?? 20)) break;
  }
  return out;
}
